import axios from "axios";
import { toast } from "sonner";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || "",
  headers: { "Content-Type": "application/json" },
});

// Rate limit tracking to avoid spamming toast messages
let lastRateLimitToast = 0;
let lastReadOnlyToast = 0;
const RATE_LIMIT_TOAST_COOLDOWN = 10000; // 10 seconds between toasts

// Attach JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Auto-inject lang=es for public endpoints when the user is on a Spanish
  // tenant microsite route ("/es/..." or "/s/<sub>/es/..."). Never overrides
  // an explicit caller-provided `lang`.
  try {
    if (typeof window !== "undefined" &&
        typeof config.url === "string" &&
        config.url.includes("/api/public/")) {
      const mid = localStorage.getItem("public_microsite_id");
      const tid = localStorage.getItem("public_tenant_id");
      if (mid && !config.headers["X-Microsite-ID"]) config.headers["X-Microsite-ID"] = mid;
      if (tid && !config.headers["X-Tenant-ID"]) config.headers["X-Tenant-ID"] = tid;
      const path = window.location.pathname || "";
      const segs = path.split("/").filter(Boolean);
      const idx = segs[0] === "s" ? 2 : 0;
      if (segs[idx] === "es") {
        config.params = config.params || {};
        if (!("lang" in config.params)) config.params.lang = "es";
      }
    }
  } catch { /* noop */ }
  return config;
});

// Token refresh state
let isRefreshing = false;
let failedQueue = [];

// Feature upgrade modal callback (set by FeatureUpgradeProvider)
let featureUpgradeCallback = null;

export function setFeatureUpgradeCallback(callback) {
  featureUpgradeCallback = callback;
}

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

// Wave 3 C5 write-through: any successful admin mutation busts the cached list queries,
// so a 60s staleTime never shows stale data after the user changes something.
const NO_INVALIDATE = ["/api/public/", "/api/tenant/i18n/insights/", "/api/analytics/track", "/api/tenant/auth/", "/api/driver/", "/api/ai/"];
const CACHED_ROOTS = ["orders", "bookings", "quotes", "catalog", "customers", "schedule", "dispatch-board", "dashboard-bundle", "admin-shell", "microsite"];
function invalidateAfterMutation(config) {
  const method = (config?.method || "get").toLowerCase();
  const url = config?.url || "";
  if (method === "get" || NO_INVALIDATE.some((p) => url.includes(p))) return;
  import("../lib/queryClient").then(({ queryClient }) => {
    queryClient.invalidateQueries({ predicate: (q) => CACHED_ROOTS.includes(q.queryKey[0]) });
  });
}

// Handle auth errors with automatic token refresh
api.interceptors.response.use(
  (response) => { invalidateAfterMutation(response.config); return response; },
  async (error) => {
    const originalRequest = error.config;
    const path = window.location.pathname;

    // Handle 429 Rate Limit errors - show friendly toast
    if (error.response?.status === 429) {
      const now = Date.now();
      const retryAfter = error.response?.data?.retry_after || 30;
      
      // Only show toast if we haven't shown one recently
      if (now - lastRateLimitToast > RATE_LIMIT_TOAST_COOLDOWN) {
        lastRateLimitToast = now;
        toast.warning("Slow down!", {
          description: `Too many requests. Please wait ${retryAfter} seconds before trying again.`,
          duration: 5000,
        });
      }
      
      // Don't redirect to login - just let the request fail gracefully
      return Promise.reject(error);
    }

    // Handle 403 feature_not_available errors
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail;
      const background = NO_INVALIDATE.some((p) => (originalRequest?.url || "").includes(p));
      // Normalise structured read-only errors to a string so downstream `toast.error(detail)` never renders an object.
      const asString = () => { error.response.data.detail = detail.message; };
      if (detail?.code === "SUPPORT_READ_ONLY") {
        const now = Date.now();
        if (now - lastReadOnlyToast > 4000) {
          lastReadOnlyToast = now;
          toast.warning("Read-only support session", { description: detail.message });
        }
        asString();
        return Promise.reject(error);
      }
      if (detail?.code === "TENANT_SUSPENDED") {
        const now = Date.now();
        if (!background && now - lastReadOnlyToast > 4000) {
          lastReadOnlyToast = now;
          toast.error("Account suspended — read-only", {
            description: "Restore your account from Billing to make changes again.",
            action: { label: "Billing", onClick: () => { window.location.href = "/admin/upgrade"; } },
          });
        }
        asString();
        return Promise.reject(error);
      }
      if (detail?.code === "TENANT_CANCELLED" && path.startsWith("/admin")) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
        localStorage.removeItem("refresh_token");
        if (path !== "/admin/login") window.location.href = "/admin/login?reason=cancelled";
        asString();
        return Promise.reject(error);
      }
      // Only show modal if:
      // 1. It's a feature_not_available error
      // 2. We have a callback
      // 3. The request explicitly wants modal (via config.showFeatureModal = true)
      //    OR fallback to showing modal (but the callback itself will check suppression)
      if (detail?.error === "feature_not_available" && featureUpgradeCallback) {
        // Always call the callback - it will internally check if suppressed
        featureUpgradeCallback(detail.feature, detail.message);
        // Still reject so the calling code knows the request failed
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for login/refresh endpoints themselves
      if (originalRequest.url?.includes("/auth/login") || originalRequest.url?.includes("/auth/refresh")) {
        return Promise.reject(error);
      }

      // Only handle refresh for admin/driver/onboarding surfaces
      if (!path.startsWith("/admin") && !path.startsWith("/driver") && !path.startsWith("/onboarding")) {
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_user");
        if (path !== "/admin/login") window.location.href = "/admin/login";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL || ""}/api/tenant/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { "Content-Type": "application/json" } }
        );
        const newToken = res.data.access_token;
        const newRefresh = res.data.refresh_token;
        localStorage.setItem("auth_token", newToken);
        if (newRefresh) localStorage.setItem("refresh_token", newRefresh);
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem("auth_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("auth_user");
        if (path !== "/admin/login") window.location.href = "/admin/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
